import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { IBackgroundModel, IBackgroundFile } from '../models/background.model';
import { apiParameters } from 'src/app/configs/api.params';
import { distinctUntilChanged } from 'rxjs/operators';



@Injectable({
  providedIn: 'root'
})
export class BackgroundService {

  public allNewBackgrounds$ = new BehaviorSubject<any>(null);
  public allNewBackgrounds = this.allNewBackgrounds$.asObservable().pipe(distinctUntilChanged());

  public newBackgrounds$ = new BehaviorSubject<any>(null);
  public newBackgrounds = this.newBackgrounds$.asObservable().pipe(distinctUntilChanged());
  //if we have two preview bg on the page
  public allNewBackgrounds2$ = new BehaviorSubject<any>(null);
  public allNewBackgrounds2 = this.allNewBackgrounds2$.asObservable().pipe(distinctUntilChanged());

  public newBackgrounds2$ = new BehaviorSubject<any>(null);
  public newBackgrounds2 = this.newBackgrounds2$.asObservable().pipe(distinctUntilChanged());

  constructor(
    private httpClient: HttpClient
  ) {}

  public setAllNewBackgrounds(bg) {
    this.allNewBackgrounds$.next(bg);
  }

  public setNewBackgrounds(bg) {
    this.newBackgrounds$.next(bg);
  }

  public setAllNewBackgrounds2(bg) {
    this.allNewBackgrounds2$.next(bg);
  }

  public setNewBackgrounds2(bg) {
    this.newBackgrounds2$.next(bg);
  }

  /**
   * This method gets all backgrounds
   * @returns List of backgrounds
   */
  getAll(): Observable<IBackgroundModel[]> {
    return this.httpClient.get(apiParameters.url + '/backgrounds')
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  /**
   * Adds background
   * @param body e.g.{ name: form.form.value.name, reflectiveFloor };
   * @returns background 
   */
  addBackground(body): Observable<IBackgroundModel> {
    return this.httpClient.post(apiParameters.url + '/backgrounds', body)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  /**
   * Deletes background
   * @param backgroundId 
   * @returns background 
   */
  deleteBackground(backgroundId: string): Observable<IBackgroundModel> {
    return this.httpClient.delete(apiParameters.url + '/backgrounds/' + backgroundId)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  deleteBackgroundFile(backgroundId: string, fileId: string): Observable<any> {
    return this.httpClient.delete(apiParameters.url + '/backgrounds/' + backgroundId + '/files/' + fileId)
      .pipe(map((res: any) => {
        return res;
      }));
  }

  updateBackground(bgId: string, body): Observable<IBackgroundModel> {
    return this.httpClient.patch(apiParameters.url + '/backgrounds/' + bgId, body)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  /**
   * Gets background files and sort them by created date
   * @param backgroundId 
   * @returns List of background files 
   */
  getBackgroundFiles(backgroundId: string): Observable<IBackgroundFile[]> {
    return this.httpClient.get(apiParameters.url + '/backgrounds/' + backgroundId + '/files')
      .pipe(map((res: IBackgroundFile[]) => {
        res.sort((a, b) => {
          return (new Date(a.createdAt) as any) - (new Date(b.createdAt) as any);
        });
        return res;
      }));
  }

  /**
   * Adds background file
   * @param formData - image file
   * @param backgroundId - backround id
   * @param parameter - parameter
   * @returns background file 
   */
  addBackgroundFile(formData: FormData, backgroundId: string, parameter: string): Observable<IBackgroundFile[]> {
    let parameterText = '';
    if (parameter && parameter !== '') {
      parameterText = '?parameter=' + parameter;
    }

    return this.httpClient.post(apiParameters.url + '/backgrounds/' + backgroundId + '/files' + parameterText, formData)
      .pipe(map((res: IBackgroundFile[]) => {
        return res;
      }));
  }

  /**
   * Gets client backgrounds
   * @param appId - client id
   * @returns List of client backgrounds 
   */
  getApplicationBackgrounds(appId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.get(apiParameters.url + '/clients/' + appId + '/backgrounds')
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  /**
   * Adds application background
   * @param appId - client id
   * @param backgroundId - background id
   * @returns application background 
   */
  addApplicationBackground(appId: string, backgroundId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.post(apiParameters.url + '/clients/' + appId + '/backgrounds', { backgroundId })
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  deleteApplicationBackground(appId: string, backgroundId: string): Observable<IBackgroundModel> {
    return this.httpClient.delete(apiParameters.url + '/clients/' + appId + '/backgrounds/' + backgroundId)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  /**
   * Gets user backgrounds
   * @param userId - user id
   * @returns List of user backgrounds 
   */
  getUserBackgrounds(userId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.get(apiParameters.url + '/users/' + userId + '/backgrounds')
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  /**
   * Gets customer backgrounds
   * @param customerId - customer id
   * @returns List of customer backgrounds 
   */
  getCustomerBackgrounds(customerId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.get(apiParameters.url + '/customers/' + customerId + '/backgrounds')
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  /**
   * Adds user backgrounds
   * @param userId - user id
   * @param backgroundId - background id
   * @returns user backgrounds 
   */
  addUserBackgrounds(userId: string, backgroundId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.post(apiParameters.url + '/users/' + userId + '/backgrounds', { backgroundId })
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  /**
   * Deletes user background
   * @param userId - user id
   * @param backgroundId - background id
   * @returns user background
   */
  deleteUserBackground(userId: string, backgroundId: string): Observable<IBackgroundModel> {
    return this.httpClient.delete(apiParameters.url + '/users/' + userId + '/backgrounds/' + backgroundId)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  addCustomerBackgrounds(customerId: string, backgroundId: string): Observable<IBackgroundModel[]> {
    return this.httpClient.post(apiParameters.url + '/customers/' + customerId + '/backgrounds', { backgroundId })
      .pipe(map((res: IBackgroundModel[]) => {
        return res;
      }));
  }

  deleteCustromerBackground(customerId: string, backgroundId: string): Observable<IBackgroundModel> {
    return this.httpClient.delete(apiParameters.url + '/customers/' + customerId + '/backgrounds/' + backgroundId)
      .pipe(map((res: IBackgroundModel) => {
        return res;
      }));
  }

  getUserBackgroundFiles(userId: string, backgroundId): Observable<IBackgroundFile[]> {
    return this.httpClient.get(apiParameters.url + '/users/' + userId + '/backgrounds/' + backgroundId + '/files')
      .pipe(map((res: IBackgroundFile[]) => {
        return res;
      }));
  }

  getApplicationBackgroundFiles(appId: string, backgroundId): Observable<IBackgroundFile[]> {

    return this.httpClient.get(apiParameters.url + '/clients/' + appId + '/backgrounds/' + backgroundId + '/files')
      .pipe(map((res: IBackgroundFile[]) => {
        return res;
      }));
  }

  // BACKGROUND REQUESTS

  addBackgroundFloor(body: FormData, customerId): Observable<any> {
    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({

      })
    };
    return this.httpClient.request(new HttpRequest('POST', apiParameters.url + '/customers/' + customerId + '/background-floors', body, httpOptions));
  }

  addBackgroundOffer(body: FormData, customerId): Observable<any> {
    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({

      })
    };

    return this.httpClient.request(new HttpRequest('POST', apiParameters.url + '/customers/' + customerId + '/background-offers', body, httpOptions));
  }

  addBackgroundWall(body: FormData, customerId): Observable<any> {
    const httpOptions = {
      reportProgress: true,
      headers: new HttpHeaders({

      })
    };

    return this.httpClient.request(new HttpRequest('POST', apiParameters.url + '/customers/' + customerId + '/background-walls', body, httpOptions));
  }

  addBackgroundRequest(body: FormData, userId): Observable<any> {
    return this.httpClient.post(apiParameters.url + '/users/' + userId + '/background-requests', body);
  }

  deleteBackgroundFloor(floorId: string, customerId): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/customers/${customerId}/background-floors/${floorId}`);
  }

  deleteBackgroundOffer(offerId: string, customerId): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/customers/${customerId}/background-offers/${offerId}`);
  }

  deleteBackgroundWall(wallId: string, customerId): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/customers/${customerId}/background-walls/${wallId}`);
  }

  getBackgroundFloors(customerId): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/background-floors`);
  }

  getBackgroundOffers(customerId): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/background-offers`);
  }

  getBackgroundWalls(customerId): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/background-walls`);
  }

  getBackgroundRequests(): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/background-requests`)
      .pipe(
        map((res: any[]) => {
          res.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  getUserBackgroundRequests(userId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/users/${userId}/background-requests`);
  }

  getBackgroundRequest(requestId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/background-requests/${requestId}`);
  }

}
